import React, { useMemo, useState } from "react";

import {
  CADENCE_ORDER,
  CadenceKey,
} from "../../Pages/GuidancePlaybooks/constants";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { updateGuidancePlaybookLinks } from "../../Store/Reducers/GuidanceSlice";
import {
  GUIDANCE_ROLES,
  GuidanceRole,
} from "../../Utils/Guidance/guidanceRole";
import { getAccessibleLinkKeysForRole } from "../../Utils/Guidance/guidanceRoleAccess";
import { toast } from "../../Utils/Helper/toast";

const CADENCE_LABEL: Record<CadenceKey, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
};

const SLOT_COUNT = 3;

const padSlots = (links: string[]): string[] => [
  links[0] || "",
  links[1] || "",
  links[2] || "",
];

/**
 * Playbook Link Assignment — cadence × role grid with a side panel for
 * editing up to 3 deep-link chips per cell. Only routes reachable for the
 * cell's role can be assigned (see guidanceRoleAccess.ts).
 */
const PlaybookLinkAssignmentTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { navLinks, playbookLinks, playbookLinksLoading, savingKey } =
    useAppSelector(state => state.guidance);

  const [editingCell, setEditingCell] = useState<{
    role: GuidanceRole;
    cadence: CadenceKey;
  } | null>(null);
  const [slots, setSlots] = useState<string[]>(["", "", ""]);

  const linksRegistry = navLinks;
  const activeLinks = linksRegistry.filter(l => l.active);
  const labelFor = (key: string) =>
    linksRegistry.find(l => l.key === key)?.label || key;
  const isActiveKey = (key: string) =>
    linksRegistry.find(l => l.key === key)?.active !== false;
  const isAssignableForRole = (role: GuidanceRole, key: string) =>
    getAccessibleLinkKeysForRole(role).includes(key);

  const getCell = (role: GuidanceRole, cadence: CadenceKey) =>
    playbookLinks.find(p => p.role === role && p.cadence === cadence);

  const assignableLinks = useMemo(() => {
    if (!editingCell) return [];
    const allowed = new Set(getAccessibleLinkKeysForRole(editingCell.role));
    return activeLinks.filter(link => allowed.has(link.key));
  }, [editingCell, activeLinks]);

  const openCell = (role: GuidanceRole, cadence: CadenceKey) => {
    const allowed = new Set(getAccessibleLinkKeysForRole(role));
    const links = (getCell(role, cadence)?.links || []).filter(key =>
      allowed.has(key)
    );
    setEditingCell({ role, cadence });
    setSlots(padSlots(links));
  };
  const closePanel = () => setEditingCell(null);

  const previewLinks = slots.filter(Boolean);
  const isSaving =
    !!editingCell && savingKey === `${editingCell.role}:${editingCell.cadence}`;

  const saveCell = () => {
    if (!editingCell) return;
    const allowed = new Set(getAccessibleLinkKeysForRole(editingCell.role));
    const invalid = previewLinks.filter(key => !allowed.has(key));
    if (invalid.length > 0) {
      toast.error(
        "One or more selected links are not available for this role. Choose only routes this role can access."
      );
      return;
    }
    const current = getCell(editingCell.role, editingCell.cadence);
    dispatch(
      updateGuidancePlaybookLinks({
        role: editingCell.role,
        cadence: editingCell.cadence,
        ...(current?.cadence_tag != null
          ? { cadence_tag: current.cadence_tag }
          : {}),
        heading: current?.heading || "",
        time: current?.time || "",
        what: current?.what || [],
        lookfor: current?.lookfor || [],
        links: previewLinks,
        ...(current?.nav != null ? { nav: current.nav } : {}),
        ...(current?.agenda != null ? { agenda: current.agenda } : {}),
      })
    );
    closePanel();
  };

  const updateSlot = (index: number, value: string) => {
    const next = [...slots];
    next[index] = value;
    setSlots(next);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Playbook Link Assignment
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Click any cell to assign deep-link chips for that role and cadence (up
          to 3 per cell).
        </p>
      </div>

      {playbookLinksLoading && playbookLinks.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-5 py-4 text-sm text-gray-500">
          Loading playbook link assignments…
        </div>
      )}

      <div
        className={`flex flex-col xl:flex-row gap-5 ${editingCell ? "xl:items-start" : ""}`}
      >
        <section className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto library-nav-scroll">
            <table
              className="border-collapse text-sm"
              style={{ minWidth: 160 + GUIDANCE_ROLES.length * 240 }}
            >
              <thead>
                <tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th
                    className="sticky left-0 z-10 bg-gray-50 border-b border-r border-gray-200 px-4 py-4 text-left"
                    style={{ width: 160, minWidth: 160 }}
                  >
                    Cadence
                  </th>
                  {GUIDANCE_ROLES.map(role => (
                    <th
                      key={role.key}
                      className="border-b border-gray-200 px-4 py-4 text-center whitespace-nowrap"
                      style={{ width: 240, minWidth: 240 }}
                      title={role.label}
                    >
                      {role.shortLabel}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CADENCE_ORDER.map(cadence => (
                  <tr key={cadence} className="group">
                    <th
                      className="sticky left-0 z-10 border-b border-r border-gray-100 px-4 py-3.5 text-left text-xs font-semibold text-gray-800 bg-white group-hover:bg-gray-50/80"
                      style={{ width: 160, minWidth: 160 }}
                    >
                      {CADENCE_LABEL[cadence]}
                    </th>
                    {GUIDANCE_ROLES.map(role => {
                      const links = getCell(role.key, cadence)?.links || [];
                      const isSelected =
                        editingCell?.role === role.key &&
                        editingCell?.cadence === cadence;
                      return (
                        <td
                          key={role.key}
                          onClick={() => openCell(role.key, cadence)}
                          style={{ width: 240, minWidth: 240 }}
                          className={`border-b border-gray-100 align-top px-3 py-3 cursor-pointer transition-colors ${
                            isSelected
                              ? "bg-purple-50 ring-2 ring-inset ring-[#974ae4]"
                              : "hover:bg-purple-50/50"
                          }`}
                        >
                          {links.length === 0 ? (
                            <span className="inline-flex text-xs text-gray-300 py-0.5">
                              {playbookLinksLoading ? "…" : "—"}
                            </span>
                          ) : (
                            <div className="flex flex-wrap gap-1 items-start">
                              {links.map(key => {
                                const allowed =
                                  isActiveKey(key) &&
                                  isAssignableForRole(role.key, key);
                                return (
                                  <span
                                    key={key}
                                    className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap ${
                                      allowed
                                        ? "bg-[#eff4ff] border border-[#bfcefe] text-[#2563eb]"
                                        : "bg-gray-50 border border-gray-200 text-gray-400 line-through"
                                    }`}
                                    title={
                                      allowed
                                        ? labelFor(key)
                                        : `${labelFor(key)} (not available for this role)`
                                    }
                                  >
                                    {labelFor(key)}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {editingCell && (
          <aside className="shrink-0 w-full xl:w-[340px] rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50/80">
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 truncate">
                  {GUIDANCE_ROLES.find(p => p.key === editingCell.role)?.label}{" "}
                  · {CADENCE_LABEL[editingCell.cadence]}
                </h4>
                <p className="mt-0.5 text-xs text-gray-500">
                  Select up to 3 links this role can access
                </p>
              </div>
              <button
                type="button"
                onClick={closePanel}
                className="shrink-0 text-gray-400 hover:text-gray-700 text-xl leading-none px-1"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {assignableLinks.length === 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                  No routes are available for this role. Update role page access
                  or the nav links registry before assigning chips.
                </div>
              ) : (
                Array.from({ length: SLOT_COUNT }).map((_, i) => (
                  <div key={i}>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Link {i + 1}{" "}
                    </label>
                    <select
                      value={slots[i]}
                      onChange={e => updateSlot(i, e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300"
                    >
                      <option value="">— none —</option>
                      {assignableLinks.map(l => (
                        <option
                          key={l.key}
                          value={l.key}
                          disabled={slots.includes(l.key) && slots[i] !== l.key}
                        >
                          {l.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))
              )}

              <div>
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Preview
                </div>
                <div className="flex flex-wrap gap-1.5 min-h-[36px] rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-3 py-2.5">
                  {previewLinks.length === 0 ? (
                    <span className="text-xs text-gray-400 self-center">
                      No links selected
                    </span>
                  ) : (
                    previewLinks.map(key => (
                      <span
                        key={key}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md border border-[#bfcefe] bg-[#eff4ff] text-[#2563eb]"
                      >
                        ↗ {labelFor(key)}
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50/50">
              <button
                type="button"
                onClick={saveCell}
                disabled={isSaving || assignableLinks.length === 0}
                className="flex-1 text-sm font-semibold text-white bg-[#974ae4] hover:bg-[#8538d4] disabled:opacity-50 rounded-lg px-3 py-2 transition-colors"
              >
                {isSaving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={closePanel}
                className="text-sm font-semibold text-gray-600 border border-gray-200 bg-white rounded-lg px-3.5 py-2 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default PlaybookLinkAssignmentTab;
